import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "bakers_admin_token";

// "Isabelle and Scott Baker" or "Isabelle & Scott" => 2 guests.
// "Mary, John and Sarah" => 3 guests. "Scott Baker" => 1.
const countGuests = (fullName) => {
  if (!fullName) return 1;
  const parts = fullName
    .trim()
    .split(/\s*(?:&|,|\s+and\s+)\s*/i)
    .filter((p) => p.trim().length > 0);
  return Math.max(1, parts.length);
};

function LoginScreen({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onSuccess(data.token);
    } catch (err) {
      toast.error(
        err?.response?.status === 401
          ? "Invalid password"
          : "Login failed. Try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-[#FAF7F2] px-6"
      style={{ fontFamily: "'Cormorant Garamond', serif" }}
    >
      <form
        onSubmit={submit}
        data-testid="admin-login"
        className="w-full max-w-sm"
      >
        <h1
          className="text-3xl uppercase tracking-[0.32em] text-[#76232F] mb-2 text-center"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          The Bakers
        </h1>
        <p className="text-[11px] uppercase tracking-[0.32em] text-[#7A6366] text-center mb-10">
          Admin dashboard
        </p>
        <label
          className="block text-[11px] uppercase tracking-[0.32em] mb-2 text-[#76232F]"
          htmlFor="admin-pw"
        >
          Password
        </label>
        <input
          id="admin-pw"
          data-testid="admin-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoFocus
          className="w-full bg-transparent border-0 border-b border-[#76232F]/40 focus:border-[#76232F] focus:outline-none py-2 text-[#2C1A1D] text-base mb-8"
        />
        <button
          type="submit"
          data-testid="admin-login-submit"
          disabled={loading}
          className="w-full py-3 border border-[#76232F] text-[#76232F] text-xs uppercase tracking-[0.32em] hover:bg-[#76232F] hover:text-[#F4EBDD] transition-colors disabled:opacity-50"
        >
          {loading ? "Signing in…" : "Enter"}
        </button>
      </form>
    </div>
  );
}

function StatCard({ label, value, testid }) {
  return (
    <div
      data-testid={testid}
      className="border border-[#E2D3C1] bg-[#FAF7F2] px-6 py-5"
    >
      <div className="text-[10px] uppercase tracking-[0.32em] text-[#76232F] mb-1">
        {label}
      </div>
      <div
        className="text-3xl text-[#2C1A1D]"
        style={{ fontFamily: "'Cormorant Garamond', serif" }}
      >
        {value}
      </div>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [rsvps, setRsvps] = useState([]);
  const [stats, setStats] = useState({ total: 0, attending: 0, not_attending: 0, transport_required: 0 });
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  useEffect(() => {
    const load = async () => {
      try {
        const [a, b] = await Promise.all([
          axios.get(`${API}/admin/rsvps`, { headers: authHeaders }),
          axios.get(`${API}/admin/stats`, { headers: authHeaders }),
        ]);
        setRsvps(a.data || []);
        setStats(b.data || {});
      } catch (err) {
        if (err?.response?.status === 401) {
          onLogout();
        } else {
          toast.error("Failed to load RSVPs");
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [authHeaders, onLogout]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rsvps.filter((r) => {
      if (filter === "yes" && r.attendance !== "yes") return false;
      if (filter === "no" && r.attendance !== "no") return false;
      if (filter === "transport" && r.transport_required !== "yes") return false;
      if (!q) return true;
      return (
        (r.full_name || "").toLowerCase().includes(q) ||
        (r.email || "").toLowerCase().includes(q) ||
        (r.dietary_requirements || "").toLowerCase().includes(q) ||
        (r.song_requests || "").toLowerCase().includes(q)
      );
    });
  }, [rsvps, search, filter]);

  const downloadCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/rsvps/export.csv`, {
        headers: authHeaders,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().slice(0, 10);
      a.download = `bakers-rsvps-${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (err) {
      toast.error("Export failed");
    }
  };

  const deleteRsvp = async (rsvp) => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/rsvps/${rsvp.id}`, {
        headers: authHeaders,
      });
      setRsvps((prev) => prev.filter((r) => r.id !== rsvp.id));
      setStats((prev) => {
        const n = countGuests(rsvp.full_name);
        const next = { ...prev };
        next.total = Math.max(0, (next.total || n) - n);
        if (rsvp.attendance === "yes") next.attending = Math.max(0, (next.attending || n) - n);
        if (rsvp.attendance === "no") next.not_attending = Math.max(0, (next.not_attending || n) - n);
        if (rsvp.transport_required === "yes") next.transport_required = Math.max(0, (next.transport_required || n) - n);
        return next;
      });
      toast.success(`Deleted ${rsvp.full_name}`);
      setPendingDelete(null);
    } catch (err) {
      toast.error("Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const fmtDate = (s) => {
    try {
      const d = new Date(s);
      return d.toLocaleString("en-AU", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return s;
    }
  };

  return (
    <div
      data-testid="admin-dashboard"
      className="min-h-screen bg-[#FAF7F2] text-[#2C1A1D]"
      style={{ fontFamily: "'Cormorant Garamond', serif" }}
    >
      {/* Header */}
      <header className="px-6 md:px-10 py-8 border-b border-[#E2D3C1] flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.32em] text-[#7A6366]">
            The Bakers — Oct 30, 2026
          </div>
          <h1 className="text-2xl md:text-3xl uppercase tracking-[0.24em] text-[#76232F] mt-1">
            RSVP responses
          </h1>
        </div>
        <button
          data-testid="admin-logout"
          onClick={onLogout}
          className="text-[11px] uppercase tracking-[0.32em] text-[#76232F] hover:opacity-70"
        >
          Sign out
        </button>
      </header>

      {/* Stats */}
      <div className="px-6 md:px-10 py-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total" value={stats.total ?? 0} testid="stat-total" />
        <StatCard label="Attending" value={stats.attending ?? 0} testid="stat-attending" />
        <StatCard label="Declined" value={stats.not_attending ?? 0} testid="stat-declined" />
        <StatCard label="Need transport" value={stats.transport_required ?? 0} testid="stat-transport" />
      </div>

      {/* Controls */}
      <div className="px-6 md:px-10 flex flex-col md:flex-row md:items-center gap-4 mb-4">
        <input
          data-testid="admin-search"
          type="text"
          placeholder="Search name, email, dietary, songs…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 bg-white border border-[#E2D3C1] focus:border-[#76232F] focus:outline-none px-4 py-2 text-base"
        />
        <select
          data-testid="admin-filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="bg-white border border-[#E2D3C1] focus:border-[#76232F] focus:outline-none px-4 py-2 text-base"
        >
          <option value="all">All replies</option>
          <option value="yes">Attending only</option>
          <option value="no">Declined only</option>
          <option value="transport">Need transport</option>
        </select>
        <button
          data-testid="admin-export"
          onClick={downloadCsv}
          className="px-6 py-2 border border-[#76232F] text-[#76232F] text-xs uppercase tracking-[0.32em] hover:bg-[#76232F] hover:text-[#F4EBDD] transition-colors"
        >
          Export CSV
        </button>
      </div>

      {/* Table */}
      <div className="px-6 md:px-10 pb-16 overflow-x-auto">
        <table className="w-full text-sm border-collapse" data-testid="admin-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.28em] text-[#76232F] border-b border-[#E2D3C1]">
              <th className="py-3 pr-4 font-normal">Submitted</th>
              <th className="py-3 pr-4 font-normal">Name</th>
              <th className="py-3 pr-4 font-normal">Email</th>
              <th className="py-3 pr-4 font-normal">Attending</th>
              <th className="py-3 pr-4 font-normal">Dietary</th>
              <th className="py-3 pr-4 font-normal">Transport</th>
              <th className="py-3 pr-4 font-normal">Songs</th>
              <th className="py-3 pr-4 font-normal text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="py-10 text-center text-[#7A6366]">
                  Loading…
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={8} className="py-10 text-center text-[#7A6366]" data-testid="admin-empty">
                  No replies yet.
                </td>
              </tr>
            ) : (
              filtered.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`admin-row-${r.id}`}
                  className="border-b border-[#E2D3C1]/60 align-top"
                >
                  <td className="py-3 pr-4 whitespace-nowrap text-[#7A6366]">
                    {fmtDate(r.submitted_at)}
                  </td>
                  <td className="py-3 pr-4 font-medium">{r.full_name}</td>
                  <td className="py-3 pr-4">{r.email}</td>
                  <td className="py-3 pr-4">
                    <span
                      className={`text-[10px] uppercase tracking-[0.24em] px-2 py-1 ${
                        r.attendance === "yes"
                          ? "bg-[#76232F]/10 text-[#76232F]"
                          : "bg-[#7A6366]/10 text-[#7A6366]"
                      }`}
                    >
                      {r.attendance === "yes" ? "Yes" : "No"}
                    </span>
                  </td>
                  <td className="py-3 pr-4">{r.dietary_requirements || "—"}</td>
                  <td className="py-3 pr-4">
                    {r.transport_required === "yes"
                      ? "Yes"
                      : r.transport_required === "no"
                      ? "No"
                      : "N/A"}
                  </td>
                  <td className="py-3 pr-4 max-w-[260px]">
                    {r.song_requests || "—"}
                  </td>
                  <td className="py-3 pr-4 whitespace-nowrap text-right">
                    <button
                      data-testid={`admin-delete-${r.id}`}
                      onClick={() => setPendingDelete(r)}
                      className="text-[10px] uppercase tracking-[0.24em] text-[#76232F] hover:opacity-70 underline-offset-4 hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        <div className="mt-4 text-[11px] uppercase tracking-[0.32em] text-[#7A6366]">
          Showing {filtered.length} of {rsvps.length}
        </div>
      </div>

      {pendingDelete && (
        <div
          data-testid="admin-delete-modal"
          className="fixed inset-0 z-50 flex items-center justify-center px-6"
        >
          <div
            className="absolute inset-0 bg-[#2C1A1D]/50"
            onClick={() => !deleting && setPendingDelete(null)}
          />
          <div className="relative bg-[#FAF7F2] border border-[#E2D3C1] max-w-md w-full p-8 shadow-xl">
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#76232F] mb-3">
              Confirm delete
            </div>
            <h2
              className="text-2xl text-[#2C1A1D] mb-3"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Are you sure?
            </h2>
            <p className="text-sm text-[#2C1A1D]/80 leading-relaxed mb-8">
              You&rsquo;re about to delete the RSVP from{" "}
              <strong className="font-semibold text-[#2C1A1D]">
                {pendingDelete.full_name}
              </strong>
              . This action cannot be undone.
            </p>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              <button
                data-testid="admin-delete-cancel"
                onClick={() => setPendingDelete(null)}
                disabled={deleting}
                className="px-5 py-2 border border-[#76232F]/40 text-[#76232F] text-xs uppercase tracking-[0.32em] hover:bg-[#76232F]/5 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                data-testid="admin-delete-confirm"
                onClick={() => deleteRsvp(pendingDelete)}
                disabled={deleting}
                className="px-5 py-2 bg-[#76232F] text-[#F4EBDD] text-xs uppercase tracking-[0.32em] hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {deleting ? "Deleting…" : "Yes, delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (!token) return <LoginScreen onSuccess={setToken} />;
  return <Dashboard token={token} onLogout={logout} />;
}
